import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Search,
  Upload,
  Trash2,
  Download,
  RefreshCw,
  X,
  File,
  FileText,
  FileSpreadsheet,
  Image,
  Archive,
  Film,
  CheckCircle,
  AlertCircle,
  Filter,
  FolderOpen,
  HardDrive,
  Eye,
  Tag,
  Calendar,
  User,
  ChevronLeft,
  ChevronRight,
  Plus,
} from 'lucide-react';

// ─── Category config ────────────────────────────────────────────────────────
const CATEGORY_MAP = {
  image:        { label: 'Image',        color: '#10b981', bg: 'rgba(16,185,129,0.15)',  icon: <Image size={14} /> },
  pdf:          { label: 'PDF',          color: '#ef4444', bg: 'rgba(239,68,68,0.15)',   icon: <FileText size={14} /> },
  spreadsheet:  { label: 'Spreadsheet',  color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', icon: <FileSpreadsheet size={14} /> },
  document:     { label: 'Document',     color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)', icon: <FileText size={14} /> },
  presentation: { label: 'Presentation', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: <Film size={14} /> },
  archive:      { label: 'Archive',      color: '#6b7280', bg: 'rgba(107,114,128,0.15)',icon: <Archive size={14} /> },
  other:        { label: 'Other',        color: '#9ca3af', bg: 'rgba(156,163,175,0.15)',icon: <File size={14} /> },
};

const getCategoryInfo = (cat) => CATEGORY_MAP[cat] || CATEGORY_MAP.other;

const getFileIcon = (category, size = 28) => {
  const color = getCategoryInfo(category).color;
  const props = { size, color };
  switch (category) {
    case 'image':        return <Image {...props} />;
    case 'pdf':          return <FileText {...props} />;
    case 'spreadsheet':  return <FileSpreadsheet {...props} />;
    case 'document':     return <FileText {...props} />;
    case 'presentation': return <Film {...props} />;
    case 'archive':      return <Archive {...props} />;
    default:             return <File {...props} />;
  }
};

// ─── Styles ─────────────────────────────────────────────────────────────────
const S = {
  container: {
    padding: '30px 40px',
    backgroundColor: '#0a0c10',
    minHeight: '100vh',
    color: '#e5e7eb',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    margin: 0,
    color: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  statIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statLabel: { fontSize: '12px', color: '#9ca3af', marginBottom: '4px' },
  statValue: { fontSize: '20px', fontWeight: '700', color: '#f9fafb' },
  filterBar: {
    backgroundColor: '#1f2937',
    padding: '16px 20px',
    borderRadius: '8px',
    border: '1px solid #374151',
    marginBottom: '20px',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchBox: { position: 'relative', flex: '1 1 240px' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 38px',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  filterSelect: {
    padding: '10px 12px',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '150px',
  },
  btn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'opacity 0.2s',
  },
  btnPrimary:   { backgroundColor: '#6366f1', color: '#fff' },
  btnDanger:    { backgroundColor: '#ef4444', color: '#fff' },
  btnSecondary: { backgroundColor: '#374151', color: '#f9fafb', border: '1px solid #4b5563' },
  btnSuccess:   { backgroundColor: '#059669', color: '#fff' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
  },
  fileCard: {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '10px',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'border-color 0.2s, transform 0.2s',
    cursor: 'default',
  },
  fileCardTop: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  fileIconBox: {
    width: '48px', height: '48px',
    backgroundColor: '#111827',
    borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  fileName: { fontSize: '14px', fontWeight: '600', color: '#f9fafb', wordBreak: 'break-word', lineHeight: 1.4 },
  fileMeta: { fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' },
  categoryBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '3px 8px', borderRadius: '20px',
    fontSize: '11px', fontWeight: '600',
  },
  fileActions: { display: 'flex', gap: '8px', marginTop: 'auto' },
  actionBtn: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '5px', padding: '7px', borderRadius: '6px',
    border: 'none', cursor: 'pointer', fontSize: '12px',
    fontWeight: '500', transition: 'opacity 0.2s',
  },
  message: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 18px', borderRadius: '6px',
    marginBottom: '20px', fontSize: '14px', fontWeight: '500',
  },
  msgSuccess: { backgroundColor: 'rgba(5,150,105,0.15)', color: '#34d399', border: '1px solid #059669' },
  msgError:   { backgroundColor: 'rgba(220,38,38,0.15)',  color: '#f87171', border: '1px solid #dc2626' },
  msgInfo:    { backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid #3b82f6' },
  dropZone: {
    border: '2px dashed #374151',
    borderRadius: '12px',
    padding: '48px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background 0.2s',
    marginBottom: '24px',
  },
  dropZoneActive: { borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.05)' },
  modal: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(4px)',
  },
  modalContent: {
    backgroundColor: '#1f2937',
    padding: '30px',
    borderRadius: '12px',
    maxWidth: '480px',
    width: '94%',
    border: '1px solid #374151',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
    position: 'relative',
  },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#f9fafb', marginBottom: '20px' },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '12px',
  },
  label: { fontSize: '12px', color: '#9ca3af', marginBottom: '6px', display: 'block' },
  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: '24px', padding: '10px 0',
  },
  pageBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '8px 12px', backgroundColor: '#1f2937',
    border: '1px solid #374151', color: '#f9fafb',
    borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
    transition: 'background 0.2s', minWidth: '38px',
  },
  pageBtnActive: { backgroundColor: '#6366f1', borderColor: '#6366f1' },
  pageBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  emptyState: {
    textAlign: 'center', padding: '60px 24px',
    color: '#6b7280',
    backgroundColor: '#1f2937',
    borderRadius: '10px',
    border: '1px solid #374151',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────
const StoragePage = () => {
  const [files, setFiles]               = useState([]);
  const [stats, setStats]               = useState({ totalFiles: 0, totalSizeDisplay: '0 B', byCategory: [] });
  const [loading, setLoading]           = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [message, setMessage]           = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm]     = useState('');
  const [filterCategory, setFilterCat] = useState('all');
  const [currentPage, setCurrentPage]  = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalCount, setTotalCount]     = useState(0);
  const [isDragging, setIsDragging]     = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null); // file object
  const [uploadForm, setUploadForm]     = useState({ description: '', tags: '' });
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const ITEMS_PER_PAGE = 12;

  // ✅ FIXED: API base URL now points to the backend server
  const api = axios.create({ baseURL: 'https://apigm.sriganapathymedical.shop/api', withCredentials: true });

  // Auto-clear message
  useEffect(() => {
    if (!message.text) return;
    const t = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => { fetchFiles(); fetchStats(); }, [currentPage, filterCategory]);

  // Re-fetch on search (debounced)
  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchFiles(); }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const showMsg = (type, text) => setMessage({ type, text });

  // ── API calls ──────────────────────────────────────────────────────────────
  const fetchFiles = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/storage/files', {
        params: {
          search: searchTerm,
          category: filterCategory === 'all' ? '' : filterCategory,
          page: currentPage,
          per_page: ITEMS_PER_PAGE,
        }
      });
      setFiles(resp.data.files || []);
      setTotalPages(resp.data.pages || 1);
      setTotalCount(resp.data.total || 0);
    } catch (err) {
      showMsg('error', '❌ Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const resp = await api.get('/storage/stats');
      setStats(resp.data);
    } catch {}
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) {
      showMsg('error', '❌ Please select at least one file');
      return;
    }

    // Get user info
    let uploadedBy = '', uploadedByName = 'System';
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      uploadedBy     = u.id || '';
      uploadedByName = u.full_name || u.name || u.username || 'System';
    } catch {}

    setUploading(true);
    let successCount = 0;
    let errorCount   = 0;

    for (const file of pendingFiles) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', uploadForm.description);
      fd.append('tags', uploadForm.tags);
      fd.append('uploadedBy', uploadedBy);
      fd.append('uploadedByName', uploadedByName);

      try {
        await api.post('/storage/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (err) {
        errorCount++;
        console.error('Upload error for', file.name, err);
      }
    }

    setUploading(false);
    setShowUploadModal(false);
    setPendingFiles([]);
    setUploadForm({ description: '', tags: '' });

    if (successCount > 0) showMsg('success', `✅ ${successCount} file(s) uploaded successfully`);
    if (errorCount   > 0) showMsg('error',   `❌ ${errorCount} file(s) failed to upload`);

    fetchFiles();
    fetchStats();
  };

  const handleDelete = async (fileObj) => {
    try {
      await api.delete(`/storage/files/${fileObj.id}`);
      showMsg('success', `✅ "${fileObj.originalFilename}" deleted`);
      setShowDeleteModal(null);
      fetchFiles();
      fetchStats();
    } catch {
      showMsg('error', '❌ Failed to delete file');
    }
  };

  // ✅ FIXED: Download URL now points to the backend server
  const handleDownload = async (fileObj) => {
    try {
      const response = await api.get(`/storage/files/${fileObj.id}/download`, {
        responseType: 'blob'
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileObj.originalFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      showMsg('error', '❌ Download failed');
    }
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length) {
      setPendingFiles(droppedFiles);
      setShowUploadModal(true);
    }
  };

  const handleFileInputChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length) {
      setPendingFiles(selected);
      setShowUploadModal(true);
    }
    e.target.value = '';
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={S.container}>

      {/* ── Message ── */}
      {message.text && (
        <div style={{ ...S.message, ...(message.type === 'success' ? S.msgSuccess : message.type === 'error' ? S.msgError : S.msgInfo) }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* ── Header ── */}
      <div style={S.header}>
        <h1 style={S.title}>
          <HardDrive size={30} color="#6366f1" />
          File Storage
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={{ ...S.btn, ...S.btnSecondary }} onClick={() => { fetchFiles(); fetchStats(); }}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            style={{ ...S.btn, ...S.btnPrimary }}
            onClick={() => { setPendingFiles([]); setShowUploadModal(true); }}
          >
            <Plus size={15} /> Upload Files
          </button>
        </div>
      </div>

      {/* ── Stats Row ── */}
      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={{ ...S.statIcon, backgroundColor: 'rgba(99,102,241,0.15)' }}>
            <FolderOpen size={22} color="#6366f1" />
          </div>
          <div>
            <div style={S.statLabel}>Total Files</div>
            <div style={S.statValue}>{stats.totalFiles ?? 0}</div>
          </div>
        </div>
        <div style={S.statCard}>
          <div style={{ ...S.statIcon, backgroundColor: 'rgba(16,185,129,0.15)' }}>
            <HardDrive size={22} color="#10b981" />
          </div>
          <div>
            <div style={S.statLabel}>Storage Used</div>
            <div style={S.statValue}>{stats.totalSizeDisplay ?? '0 B'}</div>
          </div>
        </div>
        {(stats.byCategory || []).slice(0, 3).map(c => {
          const info = getCategoryInfo(c.category);
          return (
            <div style={S.statCard} key={c.category}>
              <div style={{ ...S.statIcon, backgroundColor: info.bg }}>
                {React.cloneElement(info.icon, { size: 22 })}
              </div>
              <div>
                <div style={S.statLabel}>{info.label}s</div>
                <div style={S.statValue}>{c.count}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Drop Zone ── */}
      <div
        style={{ ...S.dropZone, ...(isDragging ? S.dropZoneActive : {}) }}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={36} color={isDragging ? '#6366f1' : '#4b5563'} style={{ marginBottom: 12 }} />
        <p style={{ fontSize: 15, color: isDragging ? '#6366f1' : '#9ca3af', margin: '0 0 6px' }}>
          {isDragging ? 'Drop files here to upload' : 'Drag & drop files here, or click to browse'}
        </p>
        <p style={{ fontSize: 12, color: '#4b5563', margin: 0 }}>
          Supports PDF, Excel, Images, Word, ZIP and more · Max 50 MB per file
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip,.rar,.txt,.json,.xml,.mp4,.mp3,.wav,.avi,.mov"
        />
      </div>

      {/* ── Filter Bar ── */}
      <div style={S.filterBar}>
        <div style={S.searchBox}>
          <Search size={15} style={S.searchIcon} />
          <input
            style={S.searchInput}
            placeholder="Search by filename, tags, description…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          style={S.filterSelect}
          value={filterCategory}
          onChange={e => { setFilterCat(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Categories</option>
          {Object.entries(CATEGORY_MAP).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        {(searchTerm || filterCategory !== 'all') && (
          <button
            style={{ ...S.btn, ...S.btnSecondary }}
            onClick={() => { setSearchTerm(''); setFilterCat('all'); setCurrentPage(1); }}
          >
            <X size={14} /> Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
          {totalCount} file{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── File Grid ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} />
          <div>Loading files…</div>
        </div>
      ) : files.length === 0 ? (
        <div style={S.emptyState}>
          <FolderOpen size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 16, marginBottom: 6 }}>No files found</div>
          <div style={{ fontSize: 13 }}>Upload your first file or clear the search filters</div>
        </div>
      ) : (
        <div style={S.grid}>
          {files.map(f => {
            const catInfo = getCategoryInfo(f.category);
            return (
              <div
                key={f.id}
                style={S.fileCard}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = '#4b5563';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#374151';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Top: icon + name */}
                <div style={S.fileCardTop}>
                  <div style={S.fileIconBox}>
                    {getFileIcon(f.category)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.fileName} title={f.originalFilename}>
                      {f.originalFilename}
                    </div>
                    <div style={S.fileMeta}>
                      <HardDrive size={10} />
                      {f.fileSizeDisplay}
                    </div>
                    <div style={S.fileMeta}>
                      <Calendar size={10} />
                      {new Date(f.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Category badge */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ ...S.categoryBadge, backgroundColor: catInfo.bg, color: catInfo.color }}>
                    {catInfo.icon}
                    {catInfo.label}
                  </span>
                  {f.fileExtension && (
                    <span style={{ ...S.categoryBadge, backgroundColor: 'rgba(255,255,255,0.05)', color: '#9ca3af' }}>
                      {f.fileExtension.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Description / tags */}
                {f.description && (
                  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>{f.description}</div>
                )}
                {f.tags && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b7280' }}>
                    <Tag size={10} />
                    {f.tags}
                  </div>
                )}

                {/* Uploader */}
                {f.uploadedByName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#4b5563' }}>
                    <User size={10} />
                    {f.uploadedByName}
                  </div>
                )}

                {/* Action buttons */}
                <div style={S.fileActions}>
                  <button
                    style={{ ...S.actionBtn, backgroundColor: '#1e3a5f', color: '#60a5fa' }}
                    onClick={() => handleDownload(f)}
                    title="Download"
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <Download size={13} /> Download
                  </button>
                  <button
                    style={{ ...S.actionBtn, backgroundColor: '#3b1f1f', color: '#f87171' }}
                    onClick={() => setShowDeleteModal(f)}
                    title="Delete"
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={S.pagination}>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>
            Page {currentPage} of {totalPages} · {totalCount} files
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              style={{ ...S.pageBtn, ...(currentPage === 1 ? S.pageBtnDisabled : {}) }}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={15} />
            </button>
            {[...Array(Math.min(totalPages, 7))].map((_, i) => {
              const p = i + 1;
              return (
                <button
                  key={p}
                  style={{ ...S.pageBtn, ...(currentPage === p ? S.pageBtnActive : {}) }}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              );
            })}
            <button
              style={{ ...S.pageBtn, ...(currentPage === totalPages ? S.pageBtnDisabled : {}) }}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── Upload Modal ── */}
      {showUploadModal && (
        <div style={S.modal} onClick={() => !uploading && setShowUploadModal(false)}>
          <div style={S.modalContent} onClick={e => e.stopPropagation()}>
            <button
              style={{ position: 'absolute', top: 15, right: 15, background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}
              onClick={() => !uploading && setShowUploadModal(false)}
            >
              <X size={20} />
            </button>
            <h3 style={S.modalTitle}>
              <Upload size={18} style={{ marginRight: 8, color: '#6366f1' }} />
              Upload Files
            </h3>

            {/* File list or picker */}
            {pendingFiles.length === 0 ? (
              <div
                style={{ ...S.dropZone, marginBottom: 16 }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={28} color="#4b5563" style={{ marginBottom: 8 }} />
                <p style={{ color: '#9ca3af', margin: '0 0 4px', fontSize: 14 }}>Click to select files</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => setPendingFiles(Array.from(e.target.files))}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip,.rar,.txt,.json,.xml"
                />
              </div>
            ) : (
              <div style={{ backgroundColor: '#111827', borderRadius: 8, padding: 12, marginBottom: 16, maxHeight: 160, overflowY: 'auto' }}>
                {pendingFiles.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < pendingFiles.length - 1 ? '1px solid #1f2937' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <File size={14} color="#6b7280" />
                      <span style={{ fontSize: 13, color: '#d1d5db' }}>{f.name}</span>
                    </div>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      {f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <label style={S.label}>Description (optional)</label>
            <input
              style={S.input}
              placeholder="Brief description of these files…"
              value={uploadForm.description}
              onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
            />

            <label style={S.label}>Tags (optional, comma-separated)</label>
            <input
              style={S.input}
              placeholder="e.g. invoice, q1-2025, supplier"
              value={uploadForm.tags}
              onChange={e => setUploadForm(p => ({ ...p, tags: e.target.value }))}
            />

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button
                style={{ ...S.btn, ...S.btnPrimary, flex: 1, justifyContent: 'center', opacity: uploading ? 0.7 : 1 }}
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading
                  ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</>
                  : <><Upload size={14} /> Upload {pendingFiles.length > 0 ? `${pendingFiles.length} File(s)` : ''}</>
                }
              </button>
              <button
                style={{ ...S.btn, ...S.btnSecondary }}
                onClick={() => { setShowUploadModal(false); setPendingFiles([]); }}
                disabled={uploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {showDeleteModal && (
        <div style={S.modal} onClick={() => setShowDeleteModal(null)}>
          <div style={S.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...S.modalTitle, color: '#f87171' }}>
              <Trash2 size={18} style={{ marginRight: 8 }} />
              Confirm Delete
            </h3>
            <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
              Are you sure you want to permanently delete{' '}
              <strong style={{ color: '#f9fafb' }}>"{showDeleteModal.originalFilename}"</strong>?
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ ...S.btn, ...S.btnDanger, flex: 1, justifyContent: 'center' }}
                onClick={() => handleDelete(showDeleteModal)}
              >
                <Trash2 size={14} /> Yes, Delete
              </button>
              <button
                style={{ ...S.btn, ...S.btnSecondary, flex: 1, justifyContent: 'center' }}
                onClick={() => setShowDeleteModal(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default StoragePage;